import audio from '@/scripts/audio';
import eventHub from '@/scripts/eventHub';
import { isRetina } from '@/scripts/utils';
import gsap from 'gsap';
import {
  Loader,
  Point,
  AnimatedSprite,
  Texture,
  Sprite,
} from 'pixi.js';
import state from '../state';
import ticker from '../ticker';
import { hitTest, hitTestCenter } from '../utils';
import vars from '../vars';
import Actor from './Actor';

class Bird extends Actor {
  constructor(parent, pipes, coins) {
    super(parent);
    this.pipesGenerator = pipes;
    this.coinsGenerator = coins;

    this.startTime = 0;
    this._invincible = false;
    this.invincibleTimeout = -1;
    this.onPowerUnFlappable = false;
    this.onPowerInvincible = false;
    this.groundOffset = 150;
    this.tutorialLoop = null;

    const emptyTexture = Texture.EMPTY;
    emptyTexture.orig.width = 60;
    emptyTexture.orig.height = 60;
    this.sprite = new Sprite(emptyTexture);
    this.body = new AnimatedSprite(
      Object.values(
        Loader.shared.resources['sprites/BIRD_BODY'].spritesheet.textures,
      ),
    );
    this.shieldBack = new AnimatedSprite(
      Object.values(
        Loader.shared.resources['sprites/BIRD_SHIELD_BACK'].spritesheet.textures,
      ),
    );
    this.shieldFore = new AnimatedSprite(
      Object.values(
        Loader.shared.resources['sprites/BIRD_SHIELD_FORE'].spritesheet.textures,
      ),
    );
    // Variants
    this.shieldBackLight = new AnimatedSprite(
      Object.values(
        Loader.shared.resources['sprites/BIRD_SHIELD_BACK_LIGHT'].spritesheet.textures,
      ),
    );
    this.shieldForeLight = new AnimatedSprite(
      Object.values(
        Loader.shared.resources['sprites/BIRD_SHIELD_FORE_LIGHT'].spritesheet.textures,
      ),
    );
    this.shieldBackHeavy = new AnimatedSprite(
      Object.values(
        Loader.shared.resources['sprites/BIRD_SHIELD_BACK_HEAVY'].spritesheet.textures,
      ),
    );
    this.shieldForeHeavy = new AnimatedSprite(
      Object.values(
        Loader.shared.resources['sprites/BIRD_SHIELD_FORE_HEAVY'].spritesheet.textures,
      ),
    );

    this.shieldFore.loop = false;
    this.shieldBack.loop = false;
    this.shieldForeLight.loop = false;
    this.shieldBackLight.loop = false;
    this.shieldForeHeavy.loop = false;
    this.shieldBackHeavy.loop = false;
    this.shieldFore.animationSpeed = 0.4;
    this.shieldBack.animationSpeed = 0.4;
    this.shieldForeLight.animationSpeed = 0.4;
    this.shieldBackLight.animationSpeed = 0.4;
    this.shieldForeHeavy.animationSpeed = 0.4;
    this.shieldBackHeavy.animationSpeed = 0.4;
    this.body.loop = false;
    this.body.animationSpeed = 0.5;
    this.shieldForeLight.renderable = false;
    this.shieldBackLight.renderable = false;
    this.shieldForeHeavy.renderable = false;
    this.shieldBackHeavy.renderable = false;

    this.sprite.addChild(this.shieldBack);
    this.sprite.addChild(this.shieldBackLight);
    this.sprite.addChild(this.shieldBackHeavy);
    this.sprite.addChild(this.body);
    this.sprite.addChild(this.shieldFore);
    this.sprite.addChild(this.shieldForeLight);
    this.sprite.addChild(this.shieldForeHeavy);
    this.parent.addChild(this.sprite);

    this.resize();

    this.invincibleTimeline = gsap.to(this.sprite, {
      alpha: 0.2,
      yoyo: true,
      repeat: -1,
      duration: 0.2,
      paused: true,
    });

    this.frameHandler = () => { this.frame(); };
    this.flapHandler = () => { this.flap(); };

    eventHub.$on('power', () => { this.power(); });
    eventHub.$on('lightstart', () => {
      this.shieldFore.renderable = false;
      this.shieldBack.renderable = false;
      this.shieldForeHeavy.renderable = false;
      this.shieldBackHeavy.renderable = false;
      this.shieldForeLight.renderable = true;
      this.shieldBackLight.renderable = true;
    });
    eventHub.$on('lightend', () => {
      this.shieldFore.renderable = true;
      this.shieldBack.renderable = true;
      this.shieldForeHeavy.renderable = false;
      this.shieldBackHeavy.renderable = false;
      this.shieldForeLight.renderable = false;
      this.shieldBackLight.renderable = false;
    });
    eventHub.$on('heavystart', () => {
      this.shieldFore.renderable = false;
      this.shieldBack.renderable = false;
      this.shieldForeHeavy.renderable = true;
      this.shieldBackHeavy.renderable = true;
      this.shieldForeLight.renderable = false;
      this.shieldBackLight.renderable = false;
    });
    eventHub.$on('heavyend', () => {
      this.shieldFore.renderable = true;
      this.shieldBack.renderable = true;
      this.shieldForeHeavy.renderable = false;
      this.shieldBackHeavy.renderable = false;
      this.shieldForeLight.renderable = false;
      this.shieldBackLight.renderable = false;
    });
  }

  get invincible() {
    return this._invincible;
  }

  set invincible(value) {
    this._invincible = value;
    if (value) this.invincibleTimeline.play();
    else {
      this.invincibleTimeline.pause();
      this.sprite.alpha = 1;
    }
  }

  resize() {
    this.sprite.anchor = new Point(0.5, 0.5);
    this.body.anchor = new Point(0.5, 0.5);
    this.shieldBack.anchor = new Point(0.5, 0.5);
    this.shieldBackLight.anchor = new Point(0.5, 0.5);
    this.shieldBackHeavy.anchor = new Point(0.5, 0.5);
    this.shieldFore.anchor = new Point(0.5, 0.5);
    this.shieldForeLight.anchor = new Point(0.5, 0.5);
    this.shieldForeHeavy.anchor = new Point(0.5, 0.5);
    // Set the start position of the bird
    // this.sprite.position.x = vars.layout.BIRD_START_X;
    // this.sprite.position.y = vars.layout.BIRD_START_Y;

    if (isRetina()) {
      this.sprite.scale = new Point(0.5, 0.5);
    }

    // Setup for the initial animation
    this.sprite.position.x = 100;
    this.sprite.position.y = 320;
    this.sprite.angle = -45;
  }

  getReady() {
    gsap.to(this.sprite, {
      angle: -10,
      ease: 'expo.out',
      duration: 0.6,
      delay: 0.2,
    });
    gsap.to(this.sprite.position, {
      x: vars.layout.BIRD_START_X,
      y: vars.layout.BIRD_START_Y,
      ease: 'elastic.out(1.5, 1)',
      duration: 1.2,
      delay: 0.2,
    });
    this.tutorialLoop = gsap.to(this.sprite.position, {
      y: vars.layout.BIRD_START_Y + 20,
      ease: 'power1.inOut',
      duration: 0.7,
      delay: 0.9,
      yoyo: true,
      repeat: -1,
    });
    this.shieldBack.loop = true;
    this.shieldFore.loop = true;
    this.shieldBack.gotoAndPlay(0);
    this.shieldFore.gotoAndPlay(0);
  }

  startFlying() {
    if (this.tutorialLoop) this.tutorialLoop.kill();
    ticker.add(this.frameHandler);
    this.startTime = ticker.totalTime;

    this.body.play();
  }

  frame() {
    if (this.onPowerUnFlappable) return;
    const fallingSeed = state.light ? 1 : state.heavy ? 3 : 2; // eslint-disable-line
    const gravity = (t) => t * t * t; // eslint-disable-line
    const duration = 30; // Proportional to the time scale
    const timeLapse = (ticker.totalTime - this.startTime) / duration;
    if (this.sprite.position.y < vars.layout.FRAME_HEIGHT - this.groundOffset) {
      this.sprite.position.y += fallingSeed * gravity(timeLapse);
      this.sprite.angle += 0.4 * gravity(timeLapse);
    }

    this.coinsGenerator.coins.forEach((coin) => {
      const isHitting = hitTest(this.sprite, coin.sprite);
      if (isHitting) {
        if (coin.catched) return;
        coin.catch();
        this.body.play();
        this.body.onComplete = () => { this.body.gotoAndStop(0); };
        const tl = gsap.timeline();
        tl.to(this.sprite.scale, {
          x: !isRetina() ? 1.4 : 0.9,
          y: !isRetina() ? 1.4 : 0.9,
          ease: 'power2.in',
          duration: 0.2,
        }).to(this.sprite.scale, {
          x: !isRetina() ? 1 : 0.5,
          y: !isRetina() ? 1 : 0.5,
          ease: 'power2.out',
          duration: 0.2,
        });
      }
    });

    if (this.invincible) return;
    if (this.onPowerInvincible) return;
    this.pipesGenerator.pipes.forEach((pipe) => {
      const isHitting = hitTestCenter(this.sprite, pipe.obstacle, this.parent);
      const isLowerThanGround = this.sprite.position.y >= vars.layout.FRAME_HEIGHT - this.groundOffset;
      if (isHitting || isLowerThanGround) {
        eventHub.$emit('shake');
        audio.play('error');
        state.hits += 1;
        state.coins = 0;
        this.invincible = true;
        this.invincibleTimeout = setTimeout(() => { this.invincible = false; }, 3000);
        if (isLowerThanGround) {
          this.sprite.position.y = vars.layout.BIRD_START_Y;
          this.startTime = ticker.totalTime;
        }
      }
    });
  }

  bindFlap(manager) {
    manager.action = this.flapHandler;
  }

  unbindFlap(manager) {
    manager.action = () => {};
  }

  flap() {
    if (this.onPowerUnFlappable) return;
    const flapPower = state.light ? 80 : state.heavy ? 20 : 50; // eslint-disable-line
    const amount = Math.max(this.sprite.position.y - flapPower, vars.layout.FRAME_TOP_DIFFERENCE);
    this.startTime = ticker.totalTime;
    gsap.to(this.sprite.position, { y: amount, ease: 'power2.out', duration: 0.2 });
    gsap.to(this.sprite, { angle: -flapPower / 2, ease: 'power2.out', duration: 0.2 });

    // Animate the sprite
    this.shieldBack.gotoAndPlay(0);
    this.shieldFore.gotoAndPlay(0);
    this.shieldBackLight.gotoAndPlay(0);
    this.shieldForeLight.gotoAndPlay(0);
    this.shieldBackHeavy.gotoAndPlay(0);
    this.shieldForeHeavy.gotoAndPlay(0);
  }

  power() {
    this.onPowerUnFlappable = true;
    this.onPowerInvincible = true;
    const duration = 2;
    const tl = gsap.timeline();
    const tempScore = { score: state.score };
    tl.addLabel('start', 1.5) // Wait for the video to complete
      .to(tempScore, {
        score: '+=50',
        ease: 'none',
        duration: 0.8,
        onUpdate: () => { state.score = Math.round(tempScore.score); },
      }, 'start+=1')
      .to(ticker, {
        speed: 30,
        ease: 'expo.in',
        duration: 0.8,
      }, 'start')
      .to(this.sprite.position, {
        x: 100,
        y: vars.layout.FRAME_HEIGHT / 2,
        ease: 'expo.in',
        duration: 0.8,
      }, 'start')
      .to(this.sprite, {
        angle: 0,
        ease: 'expo.out',
        duration: 0.8,
      }, 'start')
      .to(ticker, {
        speed: 1,
        ease: 'expo.out',
        duration: 0.4,
      }, `start+=${duration}`)
      .to(this.sprite.position, {
        x: vars.layout.BIRD_START_X,
        ease: 'expo.out',
        duration: 0.4,
      }, `start+=${duration}`)
      .add(() => {
        eventHub.$emit('powerend');
        state.coins = 0;
      }, `start+=${duration}`)
      .add(() => {
        this.onPowerUnFlappable = false;
        this.startTime = ticker.totalTime;
      }, `start+=${duration + 0.5}`)
      .add(() => {
        this.onPowerInvincible = false;
      }, `start+=${duration + 2}`);
  }

  gameover() {
    ticker.remove(this.frameHandler);
    this.sprite.renderable = false;
  }

  replay() {
    this.sprite.renderable = true;
    this.startTime = ticker.totalTime;
    this.sprite.angle = -10;
    ticker.add(this.frameHandler);
  }
}

export default Bird;
